import React, { useState } from "react";

import CopyObjectIcon from "../Buttons/CopyObjectIcon"; // Icon/button component used for copy interaction
import { formatDateTimeUTC } from "../../util/customFunctions"; // Utility function to format date

/**
 * MarketingSupportRequestsTableCopy component
 * This component generates an HTML table from the provided columns and data,
 * and allows users to copy a styled version of the table to the clipboard.
 */
function MarketingSupportRequestsTableCopy({ columns, tableData }) {
  const [isCopied, setIsCopied] = useState(false); // Flag for whether the table has been copied
  const [text, setText] = useState("Copy table to clipboard"); // Button label text

  //Symbols '▂','▁','━'
  const totalStages = tableData.length > 0 ? tableData[0].Status.values.length : 0;

  // Handles the logic for copying the table to the clipboard in rich (HTML) format
  const handleCopyClick = () => {
    const ClipboardItem = window.ClipboardItem || window.webkitClipboardItem;

    // If ClipboardItem is not supported in the browser
    if (!ClipboardItem) {
      console.log("Your browser does not support rich clipboard copy.");
      return;
    }

    // Create the table header row using column labels
    const headerRow = columns.map(
      (col, idx) =>
        `<th style="background-color: ${
          idx % 2 === 0 ? "#5b9bd5" : "#4471c4"
        }; color: white; border: 1px solid rgb(211, 211, 211); padding: 7px 10px;">
        ${col.label}
      </th>`
    );

    // Add an index column as the first header cell
    const fullHeader = `<tr>
      <th style="background-color: #4471c4; color: white; border: 1px solid rgb(211, 211, 211); padding: 7px 10px;">#</th>
      ${headerRow.join("")}
    </tr>`;

    // Create table rows from the provided tableData
    const dataRows = tableData.map((row, index) => {
      const isEven = (index + 1) % 2 === 0;
      const rowBgColor = isEven ? "#ffffff" : "#f5f5f5";

      // Generate each cell in the row
      const cells = columns.map((col) => {
        let value = "-";
        let textAlign = "left";

        if (col.dropdown) {
          // If it's a dropdown column, get the selected value
          value = row[col.field]?.value || "-";
        } else if (col.type === "date") {
          // Format date fields
          value = row[col.field] ? formatDateTimeUTC(row[col.field]) : "-";
        } else if (col.type === "Progress") {
          const completed = row.Progress || 0;
          value = `${completed} out of ${totalStages}`;
          textAlign = "right";
        } else {
          // Default rendering for text or other types
          value = row[col.field] || "-";
        }

        // Return a styled cell
        return `<td style="border: 1px solid rgb(211, 211, 211); padding: 7px 10px; font-size: 0.85rem;text-align: ${textAlign};">${value}</td>`;
      });

      // Return a full row with the index cell first
      return `<tr style="background-color: ${rowBgColor};">
        <td style="border: 1px solid rgb(211, 211, 211); padding: 7px 10px; font-size: 0.85rem;">
          ${index + 1}
        </td>
        ${cells.join("")}
      </tr>`;
    });

    // Wrap everything in a table tag
    const html = `
      <table style="border-collapse: collapse; font-family: Arial, sans-serif;">
        <thead>${fullHeader}</thead>
        <tbody>${dataRows.join("")}</tbody>
      </table>
    `;

    // Create a clipboard item with HTML content
    const blob = new Blob([html], { type: "text/html" });
    const data = [new ClipboardItem({ "text/html": blob })];

    // Attempt to write to the clipboard
    navigator.clipboard
      .write(data)
      .then(() => {
        console.log("Styled table copied to clipboard!");

        // Show success message
        setIsCopied(true);
        setText("Table copied");

        setTimeout(() => {
          setIsCopied(false);
          setText("Copy table to clipboard");
        }, 3000);
      })
      .catch((err) => {
        console.log("Failed to copy: ", err);
      });
  };

  return (
    <div>
      <button
        onClick={handleCopyClick}
        style={{ border: "none", background: "transparent", cursor: "pointer" }}
      >
        <CopyObjectIcon text={text} />
      </button>
    </div>
  );
}

export default MarketingSupportRequestsTableCopy;
